import React, { Fragment, useState, useMemo } from "react";
import { CheckIcon, ChevronDown, DatabaseZap, Lock, Search, X } from "lucide-react";
import { Listbox, Transition } from "@headlessui/react";
import { BrainBankOption } from "../../hooks/useBrainBanks";

type KnowledgeBaseBoxProps = {
  knowledgeBaseName: string | null;
  availableKnowledgeBases?: BrainBankOption[];
  onKnowledgeBaseChange?: (knowledgeBaseId: string) => void;
  /** Called when the list is opened, so the caller can reload it. */
  onOpen?: () => void;
};

// The selected BrainBank, shown in the navbar beside HatBox. Same control as the
// VA's, so the two read as one row: when a VA and a BrainBank are both picked,
// the BrainBank is the one the chat actually reads.
const KnowledgeBaseBox: React.FC<KnowledgeBaseBoxProps> = ({
  knowledgeBaseName,
  availableKnowledgeBases = [],
  onKnowledgeBaseChange,
  onOpen,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  const filteredKnowledgeBases = useMemo(() => {
    if (!searchQuery.trim()) return availableKnowledgeBases;

    return availableKnowledgeBases.filter((knowledgeBase) => {
      const name = knowledgeBase.name;
      if (!name) {
        return false;
      }
      return name.toLowerCase().includes(searchQuery.toLowerCase());
    });
  }, [availableKnowledgeBases, searchQuery]);

  if (!availableKnowledgeBases.length || !onKnowledgeBaseChange) {
    return (
      <div className="w-48 sm:w-48 lg:w-60">
        <div className="relative mt-1">
          <div className="relative w-full rounded-lg bg-surface-overlay shadow-sm py-2 pl-3 pr-10 text-left cursor-not-allowed opacity-70">
            <span className="flex items-center justify-between font-medium text-primary">
              <DatabaseZap size={20} strokeWidth={1.75} className="text-muted flex-shrink-0" />
              <span className="truncate flex-1 pl-2">
                {knowledgeBaseName || "No BrainBank"}
              </span>
              <Lock className="h-4 w-4 text-subtle mr-2" />
            </span>
          </div>
        </div>
      </div>
    );
  }

  const selectedKnowledgeBase = knowledgeBaseName
    ? availableKnowledgeBases.find((knowledgeBase) => knowledgeBase && knowledgeBase.name === knowledgeBaseName)
    : null;

  const handleKnowledgeBaseSelect = (knowledgeBaseId: string) => {
    if (onKnowledgeBaseChange) {
      onKnowledgeBaseChange(knowledgeBaseId);
    }
    setSearchQuery("");
    setIsOpen(false);
  };

  return (
    <div className="w-48 sm:w-48 lg:w-60">
      <Listbox
        value={selectedKnowledgeBase?.id || ''}
        onChange={handleKnowledgeBaseSelect}
      >
        {({ open }) => {
          // Update local state when Listbox opens/closes
          if (open !== isOpen) {
            setIsOpen(open);
            if (!open) setSearchQuery("");
          }

          return (
            <div className="relative mt-1">
              <Listbox.Button
                className="relative w-full rounded-lg bg-surface-overlay shadow-sm hover:bg-surface-2 hover:shadow py-2 pl-3 pr-10 text-left cursor-pointer transition-all duration-200"
                onClick={() => {
                  if (!open) onOpen?.();
                }}
              >
                <span className="flex items-center justify-between font-medium text-primary">
                  <DatabaseZap size={20} strokeWidth={1.75} className="text-muted flex-shrink-0" />
                  <span className={`truncate flex-1 pl-2 ${!knowledgeBaseName ? 'text-subtle italic' : ''}`}>
                    {knowledgeBaseName || "Select BrainBank..."}
                  </span>
                  <ChevronDown className="h-4 w-4 text-subtle mr-2" />
                </span>
              </Listbox.Button>

              <Transition
                as={Fragment}
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
                afterLeave={() => setSearchQuery("")}
              >
                <Listbox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-hidden rounded-md bg-surface-overlay text-primary shadow-lg ring-1 ring-border-default focus:outline-none">
                  {/* Search Input */}
                  <div className="sticky top-0 z-10 bg-surface-overlay border-b border-border-default p-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-subtle" />
                      <input
                        type="text"
                        placeholder="Search BrainBanks..."
                        className="w-full pl-10 pr-3 py-2 text-sm bg-surface-2 text-primary border border-border-default rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent placeholder:text-subtle"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  </div>

                  {/* Options List */}
                  <div className="max-h-48 overflow-auto py-1">
                    {/* Add "No BrainBank" option at the top */}
                    <Listbox.Option
                      value=""
                      className={({ active }) =>
                        `relative cursor-pointer select-none py-2 pl-10 pr-4 text-primary ${
                          active ? 'bg-surface-2' : ''
                        }`
                      }
                    >
                      {({ selected }) => (
                        <>
                          <span className={`block truncate italic text-subtle ${selected ? 'font-medium' : 'font-normal'}`}>
                            No BrainBank
                          </span>
                          {selected && !knowledgeBaseName && (
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-subtle">
                              <X className="w-4 h-4" />
                            </span>
                          )}
                        </>
                      )}
                    </Listbox.Option>

                    {/* BrainBank options */}
                    {filteredKnowledgeBases.length > 0 ? (
                      filteredKnowledgeBases.map((knowledgeBase) => (
                        <Listbox.Option
                          key={knowledgeBase.id || knowledgeBase.name}
                          value={knowledgeBase.id}
                          className={({ active }) =>
                            `relative cursor-pointer select-none py-2 pl-10 pr-4 text-primary ${
                              active ? 'bg-surface-2' : ''
                            }`
                          }
                        >
                          {({ selected }) => (
                            <>
                              <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                {knowledgeBase.name || 'Unnamed BrainBank'}
                              </span>
                              {/* Same green check the chat-side picker uses, so
                                  the two agree on what "selected" looks like. */}
                              {selected && (
                                <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                                  <CheckIcon className="text-green-500 w-4 h-4" />
                                </span>
                              )}
                            </>
                          )}
                        </Listbox.Option>
                      ))
                    ) : (
                      <div className="py-4 px-4 text-sm text-subtle text-center">
                        No BrainBanks found matching "{searchQuery}"
                      </div>
                    )}
                  </div>
                </Listbox.Options>
              </Transition>
            </div>
          );
        }}
      </Listbox>
    </div>
  );
};

export default KnowledgeBaseBox;
